<template>
  <div v-if="!loading">
    <v-row no-gutters class="pb-6">
      <v-col cols="6" class="center align-content-center align-center">
        <v-btn class="third white--text" large @click="openDialog">{{ dateStr }}</v-btn>
        <v-icon v-if="selectedDateRange.length" class="ml-2" @click="removeDateFilter"> mdi-close-circle </v-icon>
        <v-dialog v-model="dialog" class="pa-0" width="290">
          <v-card class="d-flex flex-column align-end pa-0">
            <v-card-text class="d-flex justify-center pa-0">
              <v-date-picker
                v-model="picker"
                :show-week="true"
                :first-day-of-week="1"
                range
                color="third"
              />
            </v-card-text>
            <v-card-actions class="flex-grow-1 justify-end">
              <v-btn class="third--text" text @click="closeDialog">Cancel</v-btn>
              <v-btn class="third--text" text @click="pickDate">Ok</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
      <v-col cols="6" class="d-flex center px-6">
        <v-select
          v-model="selectedProduct"
          label="Produit"
          :items="products"
          item-value="uuid"
          item-text="name"
          color="third"
          return-object
          solo
          hide-details
          clearable
        />
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="sessionsFilteredByProduct"
      :hide-default-footer="true"
      :hide-default-header="true"
      :items-per-page="-1"
      :height="tableHeight"
      class="elevation-3"
      :loading="tableLoading"
    >
      <template #header="{ props }">
        <th v-for="(header,index) in props.headers" :key="`participant_presence_${index}`" style="border-bottom: 1px solid #e2e2e2;" class="tableHeader center vertical-middle">
          <v-icon v-if="header.icon" size="24" color="black" right class="px-4">{{ header.icon }}</v-icon>
          <span>{{ header.text }}</span>
        </th>
      </template>
      <template #item.date="{ item }">
        {{ sessionDateLabel(item) }}
      </template>
      <template #item.sessionName="{ item }">
        {{ sessionNameLabel(item) }}
      </template>
      <template #item.buttons="{ item }">
        <div v-if="isPresenceToMark(item) || item.isEditing">
          <v-btn x-small color="info" @click="updatePresence(item, presenseStatus.ABSENT)">
            Absence Simple
          </v-btn>
          <v-btn x-small color="info" class="ml-2" @click="updatePresence(item, presenseStatus.PRESENT)">
            Présent
          </v-btn>
        </div>
        <div v-else>
          <v-btn v-if="wasPresent(item)" small color="grey" disabled>
            Présent
          </v-btn>
          <v-btn v-else-if="wasAbsent(item)" small color="grey" disabled>
            Absent
          </v-btn>
          <div v-else>
            <v-btn small color="grey" disabled>
              Absent
            </v-btn>
          </div>
        </div>
      </template>
      <template #item.changeSession="{ item }">
        <TransferTable :session="item" :participant="customer.uuid" />
      </template>
      <template #item.actions="{ item }">
        <v-btn v-if="!isPresenceToMark(item)" small icon @click="item.isEditing = !item.isEditing">
          <v-icon v-if="!item.isEditing">mdi-pencil</v-icon>
          <v-icon v-else>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
  <div v-else class="full-height">
    <div class="d-flex justify-center align-center">
      <Loader />
    </div>
  </div>
</template>

<script>
import { onBeforeMount, ref, computed, watch } from '@vue/composition-api';
import { format, getHours, getMonth, getYear, addDays, endOfWeek, getUnixTime, addYears, fromUnixTime, startOfDay, endOfDay, addMonths} from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import store from '@/store';
import { frenchTime, sendNotification, sortArray } from '../../../helpers/utils';
import { presenseStatus } from '../../../helpers/variables';
import { updateSessionPresence, contentTypeSessions } from '../../../helpers/amplify';
import { getCustomerSessions } from '../../../helpers/customer';
import { getCustomerYearlyProductRecuperations } from '../../../helpers/request';
import Loader from '../../common/Loader.vue';
import TransferTable from '@/components/sessions/TransferTable.vue';

export default {
  components: { Loader, TransferTable },
  props: {
    customer: {
      type: Object,
      default: null
    },
    changeCurrentView: {
      type: Function,
      default: null
    }
  },
  setup (props) {
    const selectedDate = computed(() => store.getters.selectedDate);
    const contentTypeAllSessions = computed(() => store.state.contentTypeAllSessions);
    const customerSessions = ref([]);
    const customerSessionsAux = ref([]);
    const loading = computed(() => store.state.componentLoader);
    const customerFullname = computed(() => `${props.customer.firstname} ${props.customer.lastname}`);
    const products = computed(() => store.state.products);

    const tableHeight = computed(() => window.innerHeight * 0.6);
    const tableLoading = ref(false);

    const dialog = ref(false);
    const picker = ref([format(new Date(selectedDate.value), 'yyyy-MM-dd'), format(addDays(endOfWeek(new Date()), 1), 'yyyy-MM-dd')]);
    const selectedDateRange = ref([]);
    const selectedProduct = ref(null);

    const openDialog = () => {
      dialog.value = true;
    };

    const closeDialog = () => {
      dialog.value = false;
    };

    const dateStr = computed(() => {
      switch (selectedDateRange.value.length) {
        case 1:
          return `${selectedDateRange.value[0]}`;
        case 2: {
          const start = selectedDateRange.value[0];
          const end = selectedDateRange.value[1];
          return `${start} - ${end}`;
        }
        default:
          return 'Sélectionner une plage de dates';
      }
    });

    const pickDate = async () => {
      selectedDateRange.value = picker.value;
      const start = getUnixTime(new Date(selectedDateRange.value[0]));
      const end = getUnixTime(new Date(selectedDateRange.value[1])) || getUnixTime(addYears(new Date(fromUnixTime(start)), 1));

      tableLoading.value = true;

      customerSessionsAux.value = customerSessions.value;
      const sessions = await contentTypeSessions(props.customer, start, end);
      customerSessions.value = sortArray(sessions, 'start_time');

      const response = await getCustomerYearlyProductRecuperations(props.customer.uuid, start, end);
      if (response.success) {
        recuperationAmount.value = response.data.length;
      }

      tableLoading.value = false;
      dialog.value = false;
    };

    const removeDateFilter = () => {
      selectedDateRange.value = [];
      customerSessions.value = customerSessionsAux.value;
    };

    const sessionsFilteredByProduct = computed(() => (selectedProduct.value ? customerSessions.value.filter(session => session.product_activity.some(pa => pa.product === selectedProduct.value.uuid)) : customerSessions.value));


    const presence = (session, status) => {
      const participant = session.participants.find(p => p.uuid === props.customer.uuid);

      return participant && participant.presence_status === status;
    };

    const recuperationAmount = ref(0);

    const recuperationTableLabel = computed(() => {
      const year = getYear(new Date(selectedDate.value)) + 1;
      return getMonth(new Date(selectedDate.value) >= 9) ? `${year}/${year + 1}` : `${year - 1}/${year}`;
    });

    const headers = computed(() => [
      { text: `Planning de ${customerFullname.value}`, value: 'date', icon: 'mdi-horse', align: 'center', width: '25%' },
      { text: 'Session', value: 'sessionName', align: 'center', width: '25%' },
      { text: `Récupération activée ${recuperationTableLabel.value} : ${recuperationAmount.value}`, value: 'buttons', align: 'center', width: '25%' },
      { text: 'Changement de session', value: 'changeSession', align: 'center', width: '15%' },
      { text: '', value: 'actions', width: '10%', align: 'center' }
    ]);

    watch(contentTypeAllSessions, async () => {
      customerSessions.value = getCustomerSessions(props.customer);
    });

    onBeforeMount(async () => {
      store.commit('setComponentLoader', true);
       customerSessions.value = getCustomerSessions(props.customer);
      if (!store.state.isLazyLoaded.products) {
        await store.dispatch('getProducts');
      }

      const response = await getCustomerYearlyProductRecuperations(props.customer.uuid, getUnixTime(startOfDay(addMonths(new Date(selectedDate.value), -6))), getUnixTime(endOfDay(addYears(new Date(selectedDate.value), 1))));
      if (response.success) {
        recuperationAmount.value = response.data.length;
      }
      store.commit('setComponentLoader', false);
    });

    const sessionDateLabel = session => {
      const time = frenchTime(session.start_time * 1000, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
      const date = format(new Date(time), 'EEEE dd MMMM yyyy', { locale: fr });
      return `Le ${date} à ${getHours(new Date(time))}h`;
    };

    const sessionNameLabel = session => session.name;

    const isPresenceToMark = session => {
      // !session.presence_status
      const participant = session.participants.find(p => p.uuid === props.customer.uuid);

      if (participant) {
        return !participant.presence_status;
      }

      return null;
    };

    const wasPresent = session => presence(session, presenseStatus.PRESENT);
    const wasAbsent = session => presence(session, presenseStatus.ABSENT);

    const seletectSession = ref(null);

    const updateSessionParticipantPresence = async (notification, args, session) => {
      if (props.changeCurrentView) {
        props.changeCurrentView('presence');
      }
      if (notification.type === 'success') {
        const responseUpdate = await updateSessionPresence(args);
        if (responseUpdate.success) {
          const findIndex = customerSessions.value.findIndex(s => s.id === session.id);
          if (findIndex !== -1) {
            customerSessions.value[findIndex].participants = responseUpdate.participants;
          }
        }
      }
      sendNotification(notification.type, notification.text);
    };

    const updatePresence = async (session, status) => {
      store.commit('setComponentLoader', true);
      session.isEditing = false;

      if (status === presenseStatus.ABSENT) {
        const sessionParticipantIndex = (session.participants || []).findIndex(p => p.uuid === props.customer.uuid);

        if (sessionParticipantIndex !== -1 && session.participants[sessionParticipantIndex].horse) {
          session.participants[sessionParticipantIndex].horse = null;
        }
      }

      const args = {
        participant: props.customer,
        session: session,
        status: status
      };
      const notification = {
        type: 'success',
        text: 'Présence mise à jour avec succès'
      };

      await updateSessionParticipantPresence(notification, args, session);
      sendNotification(notification.type, notification.text);
      store.commit('setComponentLoader', false);
    };

    return { headers, customerSessions, loading, sessionDateLabel, isPresenceToMark, wasPresent, wasAbsent, presenseStatus, updatePresence, sessionNameLabel, tableHeight, openDialog, closeDialog, dateStr, dialog, picker, pickDate, selectedProduct, products, removeDateFilter, selectedDateRange, sessionsFilteredByProduct, tableLoading };
  }
};
</script>
